import { useState, useEffect, useCallback } from "react";
import { BarChart3, Trophy, Star, Award, Gamepad2 } from "lucide-react";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import FootballPitch from "@/components/FootballPitch";
import api from "@/services/api";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";

export default function StatsPage() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const loadStats = useCallback(async () => {
    try {
      setIsLoading(true);
      const data = await api.getHallOfFame();
      setStats(data);
    } catch (error) {
      toast.error("Erreur lors du chargement des stats");
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadStats();
  }, [loadStats]);

  const handlePlayerClick = (playerId) => {
    if (playerId) navigate(`/profile/${playerId}`);
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="animate-spin" style={{ width: '32px', height: '32px', border: '3px solid #333', borderTopColor: '#22c55e', borderRadius: '50%' }} />
      </div>
    );
  }

  const mostMatches = stats?.most_matches;
  const mvpKing = stats?.mvp_king;
  const topScorers = stats?.top_scorers || [];
  const dreamTeam = stats?.dream_team || [];

  return (
    <div data-testid="stats-page" style={{ minHeight: '100vh', backgroundColor: '#000' }}>
      {/* Header */}
      <header style={{ paddingTop: '32px', paddingBottom: '16px', textAlign: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginBottom: '4px' }}>
          <BarChart3 size={22} color="#22c55e" />
          <h1 className="font-sport uppercase" style={{ fontSize: '22px', fontWeight: 700, color: '#fff' }}>
            Stats & Dream Team
          </h1>
        </div>
        <p style={{ color: '#6b7280', fontSize: '12px' }}>
          Classement en temps reel
        </p>
      </header>

      {/* Hall of Fame */}
      <section style={{ padding: '0 16px 20px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <Award size={16} color="#22c55e" />
          <h2 className="font-heading" style={{ fontSize: '14px', fontWeight: 600, color: '#fff' }}>
            Hall of Fame
          </h2>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {/* Most Matches */}
          {mostMatches && mostMatches.matches_played > 0 && (
            <div
              data-testid="hof-most-matches"
              onClick={() => handlePlayerClick(mostMatches.id)}
              style={{
                backgroundColor: '#111',
                border: '0.5px solid #333',
                borderRadius: '12px',
                padding: '14px 16px',
                cursor: 'pointer',
                display: 'grid',
                gridTemplateColumns: '48px 1fr auto',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: 'rgba(34,197,94,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Gamepad2 size={18} color="#22c55e" />
              </div>
              <div>
                <p style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '2px' }}>Le plus de matchs</p>
                <p style={{ fontSize: '14px', fontWeight: 600, color: '#fff' }}>{mostMatches.name}</p>
              </div>
              <div style={{ textAlign: 'right', minWidth: '70px' }}>
                <span style={{ fontSize: '18px', fontWeight: 700, color: '#22c55e', fontVariantNumeric: 'tabular-nums' }}>{mostMatches.matches_played}</span>
                <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>matchs</span>
              </div>
            </div>
          )}

          {/* MVP King */}
          {mvpKing && mvpKing.mvp_count > 0 && (
            <div
              data-testid="hof-mvp-king"
              onClick={() => handlePlayerClick(mvpKing.id)}
              style={{
                backgroundColor: '#111',
                border: '0.5px solid rgba(234,179,8,0.3)',
                borderRadius: '12px',
                padding: '14px 16px',
                cursor: 'pointer',
                display: 'grid',
                gridTemplateColumns: '48px 1fr auto',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: 'rgba(234,179,8,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                🥇
              </div>
              <div>
                <p style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '2px' }}>Roi des MVP</p>
                <p style={{ fontSize: '14px', fontWeight: 600, color: '#fff' }}>{mvpKing.name}</p>
              </div>
              <div style={{ textAlign: 'right', minWidth: '70px' }}>
                <span style={{ fontSize: '18px', fontWeight: 700, color: '#eab308', fontVariantNumeric: 'tabular-nums' }}>{mvpKing.mvp_count}</span>
                <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>MVP</span>
              </div>
            </div>
          )}

          {/* Top Scorer */}
          {stats?.top_scorer && stats.top_scorer.goals > 0 && (
            <div
              data-testid="hof-top-scorer"
              onClick={() => handlePlayerClick(stats.top_scorer.id)}
              style={{
                backgroundColor: '#111',
                border: '0.5px solid rgba(34,197,94,0.3)',
                borderRadius: '12px',
                padding: '14px 16px',
                cursor: 'pointer',
                display: 'grid',
                gridTemplateColumns: '48px 1fr auto',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <div style={{ width: '36px', height: '36px', borderRadius: '50%', backgroundColor: 'rgba(34,197,94,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '18px' }}>
                ⚽
              </div>
              <div>
                <p style={{ fontSize: '10px', color: '#6b7280', textTransform: 'uppercase', marginBottom: '2px' }}>Meilleur buteur</p>
                <p style={{ fontSize: '14px', fontWeight: 600, color: '#fff' }}>{stats.top_scorer.name}</p>
              </div>
              <div style={{ textAlign: 'right', minWidth: '70px' }}>
                <span style={{ fontSize: '18px', fontWeight: 700, color: '#22c55e', fontVariantNumeric: 'tabular-nums' }}>{stats.top_scorer.goals}</span>
                <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>buts</span>
              </div>
            </div>
          )}

          {/* Empty state */}
          {(!mostMatches || mostMatches.matches_played === 0) && (!mvpKing || mvpKing.mvp_count === 0) && (!stats?.top_scorer || stats.top_scorer.goals === 0) && (
            <div style={{ textAlign: 'center', padding: '24px 0', color: '#6b7280', fontSize: '14px' }}>
              Aucune stat pour le moment — jouez des matchs!
            </div>
          )}
        </div>
      </section>

      {/* Dream Team */}
      {dreamTeam.length > 0 && (
        <section style={{ padding: '0 16px 20px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
            <Trophy size={16} color="#22c55e" />
            <h2 className="font-heading" style={{ fontSize: '14px', fontWeight: 600, color: '#fff' }}>
              Dream Team
            </h2>
          </div>

          <div style={{ backgroundColor: '#0a0a0a', border: '0.5px solid #333', borderRadius: '16px', padding: '16px' }}>
            <FootballPitch players={dreamTeam} />
          </div>
        </section>
      )}

      {/* Top Scorers */}
      <section style={{ padding: '0 16px 24px 16px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '12px' }}>
          <Star size={16} color="#22c55e" />
          <h2 className="font-heading" style={{ fontSize: '14px', fontWeight: 600, color: '#fff' }}>
            Meilleurs Buteurs
          </h2>
        </div>

        <div style={{ display: 'flex', flexDirection: 'column', gap: '10px' }}>
          {topScorers.filter(s => s.goals > 0).map((scorer, index) => (
            <div
              key={scorer.id}
              data-testid={`scorer-${index}`}
              onClick={() => handlePlayerClick(scorer.id)}
              style={{
                backgroundColor: '#111',
                border: '0.5px solid #333',
                borderRadius: '12px',
                padding: '14px 16px',
                cursor: 'pointer',
                display: 'grid',
                gridTemplateColumns: '40px 44px 1fr auto',
                alignItems: 'center',
                gap: '10px',
              }}
            >
              <div style={{ 
                width: '28px', height: '28px', borderRadius: '50%', 
                backgroundColor: index === 0 ? 'rgba(234,179,8,0.2)' : index === 1 ? 'rgba(156,163,175,0.2)' : 'rgba(249,115,22,0.2)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: '12px', fontWeight: 700,
                color: index === 0 ? '#eab308' : index === 1 ? '#9ca3af' : '#f97316',
              }}>
                {index + 1}
              </div>
              <Avatar style={{ width: '36px', height: '36px', border: '1px solid #333' }}>
                <AvatarFallback style={{ backgroundColor: '#22c55e', color: '#fff', fontSize: '12px' }}>
                  {scorer.name?.charAt(0)}
                </AvatarFallback>
              </Avatar>
              <div>
                <p style={{ fontSize: '14px', fontWeight: 500, color: '#fff' }}>{scorer.name}</p>
                <p style={{ fontSize: '10px', color: '#6b7280' }}>{index === 0 ? 'Leader' : `${index + 1}ème`}</p>
              </div>
              <div style={{ textAlign: 'right', minWidth: '60px' }}>
                <span style={{ fontSize: '18px', fontWeight: 700, color: index === 0 ? '#22c55e' : '#fff', fontVariantNumeric: 'tabular-nums' }}>{scorer.goals}</span>
                <span style={{ fontSize: '12px', color: '#6b7280', marginLeft: '4px' }}>buts</span>
              </div>
            </div>
          ))}
          
          {topScorers.filter(s => s.goals > 0).length === 0 && (
            <div style={{ textAlign: 'center', padding: '32px 0', color: '#6b7280' }}>
              Aucun buteur pour le moment
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
